'use client';

import { useCallback } from 'react';
import { ROLE_TYPES, STREAMING_EVENT_TYPES } from '../constants';
import type { ChatMessage } from '../types';
import { type ActivePlannerReportEntry, useActivePlannerReports } from './useActivePlannerReports';

const KICKOFF_MESSAGE_TEXT =
    'Your report is now being generated. This can take 15-30 minutes depending on the scope.\n\nYou can leave this page and come back anytime — your report will be waiting for you.';

interface UseReportKickoffConfig {
    currentConversationId: string | null;
    addMessage: (msg: ChatMessage) => void;
    setPlannerReportData: (
        data: { status?: string; reportId?: number; reportUrl?: string } | null,
    ) => void;
    setPlannerRunId: (runId: string | null) => void;
    setPlannerPhase: (phase: 'report' | null) => void;
}

export interface UseReportKickoffApi {
    /**
     * Single source of truth for marking a report as kicked off. Idempotent
     * on `runId` — if the run is already tracked the canned message is not
     * re-added and polling is not re-started, which keeps the three trigger
     * paths (PlanSummaryCard button, depth selector, agent SSE event) safe
     * to fire in any order.
     */
    handleReportKickedOff: (runId: string, brandLabel?: string) => void;
    getActiveReportEntry: (
        runId: string | null | undefined,
    ) => ActivePlannerReportEntry | undefined;
}

export function useReportKickoff({
    currentConversationId,
    addMessage,
    setPlannerReportData,
    setPlannerRunId,
    setPlannerPhase,
}: UseReportKickoffConfig): UseReportKickoffApi {
    const { upsert: upsertActiveReport, getEntry: getActiveReportEntry } =
        useActivePlannerReports();

    const handleReportKickedOff = useCallback<UseReportKickoffApi['handleReportKickedOff']>(
        (runId, brandLabel) => {
            if (!runId) return;
            if (getActiveReportEntry(runId)) return;

            addMessage({
                id: `report_kickoff_${runId}`,
                role: ROLE_TYPES.ASSISTANT,
                content: [
                    {
                        type: STREAMING_EVENT_TYPES.TEXT,
                        text: KICKOFF_MESSAGE_TEXT,
                    },
                ],
                isStreaming: false,
                timestamp: new Date(),
            });

            setPlannerReportData(null);
            setPlannerRunId(runId);
            setPlannerPhase('report');

            if (currentConversationId) {
                upsertActiveReport({
                    runId,
                    conversationId: currentConversationId,
                    ...(brandLabel ? { brandLabel } : {}),
                });
            }
        },
        [
            addMessage,
            currentConversationId,
            getActiveReportEntry,
            setPlannerPhase,
            setPlannerReportData,
            setPlannerRunId,
            upsertActiveReport,
        ],
    );

    return { handleReportKickedOff, getActiveReportEntry };
}
